import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from '../../../hooks';
import proofkitFilemakerService from '../../../services/proofkitFilemakerService';
import { RefreshCw, Plus } from "lucide-react";
import { Button } from '../../../components/ui';
import { YoolTable, StatusPill, RowActions, IconButton } from '../../../components/ui/YoolTable';

const SEGMENTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_SEGMENTS || "segments";
const CONTACT_SEGMENTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CONTACT_SEGMENTS || "contact_segments";

export default function SegmentsList() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [segments, setSegments] = useState([]);
  const [contactSegments, setContactSegments] = useState([]);

  const fetchSegments = async () => {
    try {
      setLoading(true);
      const [segmentsRes, contactSegmentsRes] = await Promise.all([
        proofkitFilemakerService.getAllRecords(SEGMENTS_LAYOUT),
        proofkitFilemakerService.getAllRecords(CONTACT_SEGMENTS_LAYOUT),
      ]);

      setSegments(Array.isArray(segmentsRes?.data) ? segmentsRes.data : []);
      setContactSegments(Array.isArray(contactSegmentsRes?.data) ? contactSegmentsRes.data : []);
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los segmentos desde FileMaker",
        variant: "destructive",
      });
      setSegments([]);
      setContactSegments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSegments();
  }, []);

  const segmentsData = useMemo(() => {
    const contactCountBySegment = {};
    contactSegments.forEach((row) => {
      const fd = row?.fieldData || {};
      const segmentId = fd?.segment_id;
      if (segmentId) {
        contactCountBySegment[segmentId] = (contactCountBySegment[segmentId] || 0) + 1;
      }
    });

    return (Array.isArray(segments) ? segments : []).map((r) => {
      const fieldData = r?.fieldData || {};
      const key = fieldData?.ClavePrincipal || "";
      return {
        id: r?.recordId || "",
        key,
        nombre: fieldData?.nombre || "",
        descripcion: fieldData?.descripcion || "",
        activo: fieldData?.activo,
        contactCount: contactCountBySegment[key] || 0,
      };
    });
  }, [segments, contactSegments]);

  const handleDeleteSegment = async (segmentId) => {
    if (!window.confirm("¿Estás seguro de eliminar este segmento?")) {
      return;
    }

    try {
      await proofkitFilemakerService.deleteRecord(SEGMENTS_LAYOUT, segmentId);
      toast({
        title: "Segmento eliminado",
        description: "El segmento fue eliminado exitosamente",
      });
      fetchSegments();
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el segmento",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-3xl font-bold">Segmentos</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchSegments} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
          <Button onClick={() => navigate("/campanas/segments/new")}>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Segmento
          </Button>
        </div>
      </div>

      <YoolTable
        columns={[
          { key: 'nombre',       label: 'Nombre',    sortable: true },
          { key: 'descripcion',  label: 'Descripción', wrap: true },
          { key: 'contactCount', label: 'Contactos', align: 'right', numeric: true, sortable: true,
            render: s => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{s.contactCount} contactos</span> },
          { key: 'activo', label: 'Activo',
            render: s => <StatusPill estado={Number(s.activo) === 1 ? 'activo' : 'inactivo'} /> },
          { key: 'acciones', label: '', align: 'right',
            render: s => (
              <RowActions>
                <IconButton icon="eye"   title="Ver"      onClick={() => navigate(`/campanas/segments/${s.id}`)} />
                <IconButton icon="edit"  title="Editar"   onClick={() => navigate(`/campanas/segments/${s.id}/edit`)} />
                <IconButton icon="trash" title="Eliminar" danger onClick={() => handleDeleteSegment(s.id)} />
              </RowActions>
            )},
        ]}
        rows={loading ? [] : segmentsData}
        rowKey="id"
        search={{ placeholder: 'Buscar segmentos…', keys: ['nombre', 'descripcion'] }}
        empty={loading
          ? { icon: 'refresh', title: 'Cargando segmentos…', text: '' }
          : { icon: 'search', title: 'Sin segmentos', text: 'No hay segmentos creados aún.' }}
      />
    </div>
  );
}
